<template>
  <div class="blog-link-suggestion">
    <CCard>
      <CCardHeader>
        <CRow>
          <CCol md="8">
            <strong>🔗 پیشنهادات لینک‌سازی لحظه‌ای</strong>
            <small class="d-block text-muted">
              بر اساس محتوای در حال نوشته شدن، پیشنهادات لینک‌سازی را دریافت کنید
            </small>
          </CCol>
          <CCol md="4" class="text-left">
            <CBadge color="info" class="mr-1">
              {{ totalSuggestions }} پیشنهاد
            </CBadge>
            <CBadge :color="getHealthColor()">
              {{ getHealthStatus() }}
            </CBadge>
          </CCol>
        </CRow>
      </CCardHeader>

      <CCardBody>
        <!-- وضعیت فعلی مقاله -->
        <CRow class="mb-3">
          <CCol md="2">
            <CCard class="text-center bg-light">
              <CCardBody>
                <div class="text-value-lg text-primary">{{ wordCount }}</div>
                <div>تعداد کلمات</div>
                <small :class="getWordCountClass(wordCount)">{{ getWordCountLabel(wordCount) }}</small>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="2">
            <CCard class="text-center bg-light">
              <CCardBody>
                <div class="text-value-lg text-success">{{ internalLinks }}</div>
                <div>لینک‌های داخلی</div>
                <small :class="getInternalLinkClass(internalLinks)">{{ getInternalLinkLabel(internalLinks) }}</small>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="2">
            <CCard class="text-center bg-light">
              <CCardBody>
                <div class="text-value-lg text-warning">{{ externalLinks }}</div>
                <div>لینک‌های خارجی</div>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="2">
            <CCard class="text-center bg-light">
              <CCardBody>
                <div class="text-value-lg text-info">{{ linkDensity }}</div>
                <div>چگالی لینک</div>
                <small class="text-muted">لینک در ۱۰۰۰ کلمه</small>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="2">
            <CCard class="text-center" :class="getDensityCardClass(keywordDensity)">
              <CCardBody>
                <div class="text-value-lg" :class="getDensityClass(keywordDensity)">
                  {{ keywordDensity }}%
                </div>
                <div>چگالی کلمه کلیدی</div>
                <small :class="getDensityClass(keywordDensity)">{{ getDensityLabel(keywordDensity) }}</small>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="2">
            <CCard class="text-center" :class="getCardQualityClass(contentQuality)">
              <CCardBody>
                <div class="text-value-lg">{{ contentQuality }}%</div>
                <div>کیفیت محتوا</div>
                <CProgress :value="contentQuality" :max="100" :color="getScoreColor(contentQuality)" height="6px" class="mt-1" />
                <small :class="getScoreClass(contentQuality)">{{ getQualityLabel(contentQuality) }}</small>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <!-- متریک‌های کیفیت محتوا (جدید) -->
        <CRow class="mb-3">
          <CCol md="4">
            <CCard class="text-center" :class="getCardQualityClass(contentQuality)">
              <CCardBody>
                <div class="text-value-lg">{{ contentQuality }}%</div>
                <div>کیفیت محتوا</div>
                <CProgress :value="contentQuality" :max="100" :color="getScoreColor(contentQuality)" height="6px" class="mt-1" />
                <small :class="getScoreClass(contentQuality)">{{ getQualityLabel(contentQuality) }}</small>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="4">
            <CCard class="text-center" :class="getCardQualityClass(readabilityScore)">
              <CCardBody>
                <div class="text-value-lg">{{ readabilityScore }}%</div>
                <div>خوانایی</div>
                <CProgress :value="readabilityScore" :max="100" :color="getScoreColor(readabilityScore)" height="6px" class="mt-1" />
                <small :class="getScoreClass(readabilityScore)">{{ getReadabilityLabel(readabilityScore) }}</small>
              </CCardBody>
            </CCard>
          </CCol>
          <CCol md="4">
            <CCard class="text-center" :class="getCardQualityClass(technicalSEO)">
              <CCardBody>
                <div class="text-value-lg">{{ technicalSEO }}%</div>
                <div>سئو تکنیکال</div>
                <CProgress :value="technicalSEO" :max="100" :color="getScoreColor(technicalSEO)" height="6px" class="mt-1" />
                <small :class="getScoreClass(technicalSEO)">{{ getTechnicalLabel(technicalSEO) }}</small>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        <!-- جزئیات کیفیت محتوا -->
        <CCard class="mb-3" v-if="contentDetails">
          <CCardBody>
            <CRow>
              <CCol md="6">
                <h6>📐 ساختار محتوا</h6>
                <table class="table table-sm table-borderless">
                  <tr>
                    <td><strong>H1</strong></td>
                    <td :class="contentDetails.h1Count === 1 ? 'text-success' : 'text-danger'">
                      {{ contentDetails.h1Count || 0 }}
                      <small>{{ contentDetails.h1Count === 1 ? '✅' : '⚠️' }}</small>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>H2</strong></td>
                    <td :class="contentDetails.h2Count >= 4 ? 'text-success' : 'text-warning'">
                      {{ contentDetails.h2Count || 0 }}
                      <small>{{ contentDetails.h2Count >= 4 ? '✅ خوب' : contentDetails.h2Count > 0 ? '⚠️ کم' : '❌' }}</small>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>H3</strong></td>
                    <td>{{ contentDetails.h3Count || 0 }}</td>
                  </tr>
                  <tr>
                    <td><strong>ترتیب هدینگ</strong></td>
                    <td>
                      <CBadge :color="contentDetails.hierarchy ? 'success' : 'danger'">
                        {{ contentDetails.hierarchy ? '✅ صحیح' : '❌ خطا' }}
                      </CBadge>
                    </td>
                  </tr>
                </table>
              </CCol>
              <CCol md="6">
                <h6>🖼️ تصاویر و رسانه</h6>
                <table class="table table-sm table-borderless">
                  <tr>
                    <td><strong>تعداد تصاویر</strong></td>
                    <td :class="contentDetails.imageCount >= 4 ? 'text-success' : 'text-warning'">
                      {{ contentDetails.imageCount || 0 }}
                      <small>{{ contentDetails.imageCount >= 4 ? '✅ عالی' : contentDetails.imageCount > 0 ? '⚠️ کم' : '❌' }}</small>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>تصاویر با Alt</strong></td>
                    <td :class="contentDetails.imagesWithAlt === contentDetails.imageCount && contentDetails.imageCount > 0 ? 'text-success' : 'text-warning'">
                      {{ contentDetails.imagesWithAlt || 0 }} از {{ contentDetails.imageCount || 0 }}
                      <small>{{ contentDetails.imagesWithAlt === contentDetails.imageCount && contentDetails.imageCount > 0 ? '✅' : '⚠️' }}</small>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>متا توضیحات</strong></td>
                    <td>
                      <CBadge :color="contentDetails.hasMetaDescription ? 'success' : 'danger'">
                        {{ contentDetails.hasMetaDescription ? '✅ دارد' : '❌ ندارد' }}
                      </CBadge>
                      <small v-if="contentDetails.hasMetaDescription" class="d-block text-muted">
                        طول: {{ contentDetails.metaDescriptionLength || 0 }} کاراکتر
                      </small>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>کلمه کلیدی در H1</strong></td>
                    <td>
                      <CBadge :color="contentDetails.keywordInH1 ? 'success' : 'warning'">
                        {{ contentDetails.keywordInH1 ? '✅ دارد' : '⚠️ ندارد' }}
                      </CBadge>
                    </td>
                  </tr>
                </table>
              </CCol>
            </CRow>

            <!-- بخش چگالی کلمه کلیدی (جدید) -->
            <CRow class="mt-3">
              <CCol md="12">
                <h6>🔑 چگالی کلمه کلیدی</h6>
                <div class="density-details">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>کلمه کلیدی:</strong>
                      <code class="mr-2">{{ keyword || 'تعیین نشده' }}</code>
                      <CBadge :color="getDensityBadgeColor(keywordDensity)" class="mr-1">
                        {{ keywordDensity }}%
                      </CBadge>
                      <small :class="getDensityClass(keywordDensity)">{{ getDensityLabel(keywordDensity) }}</small>
                    </div>
                    <div>
                      <small class="text-muted">
                        تعداد تکرار: {{ keywordCount }} بار
                      </small>
                    </div>
                  </div>
                  <CProgress
                      :value="keywordDensity"
                      :max="5"
                      :color="getDensityProgressColor(keywordDensity)"
                      height="8px"
                      class="mt-1"
                  />
                  <div class="d-flex justify-content-between mt-1">
                    <small class="text-muted">0%</small>
                    <small class="text-success">✅ محدوده ایده‌آل (1-3%)</small>
                    <small class="text-muted">5%</small>
                  </div>
                  <div class="mt-2">
                    <small class="text-muted">
                      💡 چگالی ایده‌آل برای کلمه کلیدی بین <strong>1 تا 3 درصد</strong> است.
                      {{ keywordDensity > 3 ? 'چگالی بالاست، سعی کنید کلمه کلیدی را کمتر تکرار کنید.' : '' }}
                      {{ keywordDensity < 1 ? 'چگالی پایین است، سعی کنید کلمه کلیدی را بیشتر تکرار کنید.' : '' }}
                    </small>
                  </div>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- هشدارها و خطاها -->
        <div v-if="warnings.length > 0" class="mb-3">
          <CAlert
              v-for="(warning, index) in warnings"
              :key="index"
              :color="warning.type"
              class="d-flex justify-content-between align-items-center"
          >
            <div>
              <CIcon :name="warning.icon" />
              {{ warning.message }}
            </div>
            <CButton
                v-if="warning.action"
                color="link"
                size="sm"
                @click="applySuggestion(warning)"
            >
              {{ warning.actionLabel }}
            </CButton>
          </CAlert>
        </div>

        <!-- پیشنهادات لینک‌سازی -->
        <div v-if="linkSuggestions.length > 0">
          <h6 class="mt-3">💡 پیشنهادات لینک‌سازی</h6>
          <p class="text-muted small">
            از مقاله جدید ({{ countKeywords(this.keyword) }} کلمه) به صفحات با ۱ کلمه کمتر لینک دهید
          </p>
          <CListGroup>
            <CListGroupItem
                v-for="(suggestion, index) in linkSuggestions"
                :key="index"
                class="d-flex justify-content-between align-items-center"
            >
              <div>
                <CBadge :color="suggestion.priority === 'high' ? 'danger' : 'warning'" class="mr-2">
                  {{ suggestion.priority === 'high' ? 'اولویت بالا' : 'اولویت متوسط' }}
                </CBadge>
                <span class="mr-2">{{ suggestion.message }}</span>
                <small class="text-muted">
                  لنگر: <code>"{{ suggestion.anchor }}"</code>
                </small>
                <br>
                <small class="text-success">
                  ✅ ارتباط موضوعی: {{ suggestion.relationType }}
                </small>
              </div>
              <div>{{suggestion.targetUrl}}</div>
              <CButton
                  color="primary"
                  size="sm"
                  @click="insertLink(suggestion)"
              >
                📝 درج لینک
              </CButton>
            </CListGroupItem>
          </CListGroup>
        </div>

        <!-- آنالیز لینک‌های موجود -->
        <div v-if="existingLinks.length > 0" class="mt-3">
          <h6>🔍 آنالیز لینک‌های موجود</h6>
          <CListGroup>
            <CListGroupItem
                v-for="(link, index) in existingLinks"
                :key="index"
                class="d-flex justify-content-between align-items-center"
                :class="{'list-group-item-success': link.isValid, 'list-group-item-danger': !link.isValid}"
            >
              <div>
                <CIcon :name="link.isValid ? 'cil-check' : 'cil-x'"
                       :class="link.isValid ? 'text-success' : 'text-danger'" />
                <span class="mr-2">{{ link.text }}</span>
                <CBadge :color="link.isValid ? 'success' : 'danger'" class="mr-1">
                  {{ link.isValid ? '✅ صحیح' : '❌ نیاز به اصلاح' }}
                </CBadge>
                <small class="text-muted">
                  → {{ link.target }} ({{link.href}})
                </small>
              </div>
              <div v-if="!link.isValid">
                <CButton
                    color="warning"
                    size="sm"
                    @click="fixLink(link)"
                >
                  🔧 اصلاح
                </CButton>
              </div>
            </CListGroupItem>
          </CListGroup>
        </div>

        <!-- وضعیت خالی -->
        <div v-if="totalSuggestions === 0 && warnings.length === 0 && existingLinks.length === 0 && contentQuality < 100" class="text-center py-5">
          <CIcon name="cil-check" size="xl" class="text-success" />
          <p class="mt-2">✨ همه چیز عالی است! لینک‌سازی شما در بهترین حالت است.</p>
        </div>
      </CCardBody>
    </CCard>

    <!-- مودال درج لینک -->
    <CModal
        :show.sync="showInsertModal"
        @close="showInsertModal = false"
        size="lg"
    >
      <CCard>
        <CCardHeader>
          📝 درج لینک در محتوا
        </CCardHeader>
        <CCardBody>
          <div v-if="selectedSuggestion">
            <h6>پیشنهاد:</h6>
            <p class="text-primary">{{ selectedSuggestion.suggestion }}</p>

            <h6>لنگر پیشنهادی:</h6>
            <div class="bg-light p-2 rounded mb-3">
              <code>{{ selectedSuggestion.anchor }}</code>
            </div>

            <h6>متن پیشنهادی:</h6>
            <div class="bg-info text-white p-2 rounded mb-3">
              {{ selectedSuggestion.sampleText }}
            </div>

            <h6>کد HTML:</h6>
            <div class="bg-dark text-white p-2 rounded mb-3">
              <code>
                &lt;a href="{{ selectedSuggestion.targetUrl }}"&gt;{{ selectedSuggestion.anchor }}&lt;/a&gt;
              </code>
            </div>

            <CButton
                color="success"
                block
                @click="copyToClipboard(selectedSuggestion)"
            >
              📋 کپی در کلیپ‌بورد
            </CButton>
          </div>
        </CCardBody>
        <CCardFooter>
          <CButton color="secondary" @click="showInsertModal = false">
            بستن
          </CButton>
        </CCardFooter>
      </CCard>
    </CModal>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: 'BlogLinkSuggestion',
  props: {
    content: {
      type: String,
      default: ''
    },
    keyword: {
      type: String,
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    metaDescription: {
      type: String,
      default: ''
    },
    slug: {
      type: String,
      default: ''
    },
    stopWords: {
      type: Array,
      default: () => [
        'از', 'با', 'برای', 'در', 'به', 'بر', 'روی', 'زیر', 'کنار',
        'جلوی', 'پشت', 'داخل', 'خارج', 'بالای', 'پایین', 'نزدیک',
        'دور', 'بین', 'میان', 'طریق', 'وسیلۀ', 'توسط', 'بجز', 'غیر',
        'مثل', 'مانند', 'همانند', 'چون', 'چرا', 'زیرا', 'چراکه',
        'که', 'تا', 'مگر', 'آیا', 'چه', 'چگونه', 'چطور', 'کجا',
        'کی', 'چند', 'چقدر', 'کدام', 'کدامین', 'این', 'آن', 'اینها',
        'آنها', 'ما', 'شما', 'او', 'ایشان', 'خود', 'خودی', 'یک', 'دو',
        'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه', 'ده', 'صد', 'هزار',
        'و', 'یا', 'اما', 'پس', 'پس‌از', 'پیش', 'پیش‌از', 'بعد', 'بعداز'
      ]
    }
  },
  data() {
    return {
      last_anylise_content:0,
      allArticles: [],
      showInsertModal: false,
      selectedSuggestion: null,
      warnings: [],
      linkSuggestions: [],
      existingLinks: [],
      wordCount: 0,
      internalLinks: 0,
      externalLinks: 0,
      linkDensity: 0,
      isLoading: false,
      // متریک‌های جدید
      contentQuality: 0,
      readabilityScore: 0,
      technicalSEO: 0,
      keywordDensity: 0,
      keywordCount: 0,
      contentDetails: {
        h1Count: 0,
        h2Count: 0,
        h3Count: 0,
        hierarchy: false,
        imageCount: 0,
        imagesWithAlt: 0,
        hasMetaDescription: false,
        metaDescriptionLength: 0,
        keywordInH1: false,
        keywordInMeta: false,
        keywordInTitle: false,
        keywordInUrl: false
      }
    };
  },
  computed: {
    totalSuggestions() {
      return this.linkSuggestions.length;
    }
  },
  watch: {
    content: {
      handler() {
        this.analyzeContent();
      },
      immediate: true,
      deep: true
    },
    allArticles: {
      handler(newVal) {
        this.analyzeContent();
      },
      deep: true
    },
    keyword: {
      handler() {
        this.analyzeContent();
      },
      immediate: true
    },
    metaDescription: {
      handler() {
        this.analyzeContent();
      },
      immediate: true
    },
    slug: {
      handler() {
        this.analyzeContent();
      },
      immediate: true
    }
  },
  mounted() {
    this.get_blogs_seo();
  },
  methods: {
    get_blogs_seo() {
      var self = this;
      this.isLoading = true;
      var form_data = new FormData();

      axios.post('/api/admin/seo_report/get_blogs_seo', form_data, {})
          .then(function (response) {
            console.log('✅ دریافت مقالات:', response.data.posts.length);

            response.data.posts.forEach(function (val) {
              const keywordCount = self.countKeywords(val.keyword || '');

              self.allArticles.push({
                type: 'article',
                htmlContent: val.description ? JSON.parse(val.description) : '',
                keyword: val.keyword || '',
                title: val.title || '',
                seo_title: val.seo_title || '',
                url: '/blog/' + (val.slug || ''),
                keywordCount: keywordCount
              });
            });

            self.isLoading = false;
            self.analyzeContent();
          })
          .catch(function (error) {
            console.error('❌ خطا در دریافت مقالات:', error);
            self.isLoading = false;
          });
    },

    analyzeContent() {
      if(new Date().getTime()+15000 < this.last_anylise_content){
        return;
      }
      console.log('🔍 analyzing content...');

      if (!this.content) {
        console.log('⚠️ محتوایی وجود ندارد');
        this.resetData();
        return;
      }
      this.last_anylise_content = new Date().getTime();

      // پارس کردن HTML
      const parser = new DOMParser();
      const fullHtml = `
<html>
<head>
    <meta name="description" content="${this.metaDescription || ''}">
    <title>${this.title || ''}</title>
</head>
<body>
    <h1>${this.title || ''}</h1>
    ${this.content || ''}
</body>
</html>
`;
      const doc = parser.parseFromString(fullHtml, 'text/html');

      // شمارش کلمات
      const textContent = doc.body.textContent || '';
      this.wordCount = this.countKeywords(textContent);

      // آنالیز لینک‌ها
      this.analyzeLinks(doc);

      // پیدا کردن پیشنهادات لینک‌سازی
      this.findLinkSuggestions(doc);

      // بررسی کیفیت محتوا
      this.analyzeContentQuality(doc);

      // بررسی خوانایی
      this.analyzeReadability(doc);

      // بررسی سئو تکنیکال
      this.analyzeTechnicalSEO(doc);

      // بررسی چگالی کلمه کلیدی
      this.analyzeKeywordDensity(doc);

      // بررسی هشدارها
      this.checkWarnings();

      // محاسبه چگالی لینک
      this.linkDensity = this.wordCount > 0 ? Math.round((this.internalLinks / this.wordCount) * 1000 * 100) / 100 : 0;

      console.log('✅ تحلیل کامل شد.');
    },

    // ==========================================
    // 1. تحلیل چگالی کلمه کلیدی (جدید)
    // ==========================================
    analyzeKeywordDensity(doc) {
      if (!this.keyword || this.wordCount === 0) {
        this.keywordDensity = 0;
        this.keywordCount = 0;
        return;
      }

      const textContent = doc.body.textContent || "";

      // حذف کلمات ربط از کلمه کلیدی
      const cleanKeyword = this.removeStopWords(this.keyword).join(' ');
      const normalizedText = textContent.toLowerCase();
      const normalizedKeyword = cleanKeyword.toLowerCase();

      let keywordCount = 0;

      // روش 1: جستجوی عبارت کامل
      const regex = new RegExp(normalizedKeyword.replace(/\s+/g, '\\s+'), 'gi');
      const matches = normalizedText.match(regex);
      keywordCount = matches ? matches.length : 0;

      // اگر با عبارت کامل چیزی پیدا نشد، کلمات جداگانه را بررسی کن
      if (keywordCount === 0) {
        const keywordWords = this.keyword.toLowerCase().split(/\s+/);
        let totalMatches = 0;
        let validWords = 0;

        keywordWords.forEach(kw => {
          const cleanKw = this.removeStopWords(kw).join('');
          if (cleanKw && cleanKw.length > 1) {
            const wordRegex = new RegExp(cleanKw, 'gi');
            const wordMatches = normalizedText.match(wordRegex);
            if (wordMatches) {
              totalMatches += wordMatches.length;
              validWords++;
            }
          }
        });

        if (validWords > 0) {
          keywordCount = Math.round(totalMatches / validWords);
        }
      }

      // اگر باز هم چیزی پیدا نشد، از روش ساده استفاده کن
      if (keywordCount === 0) {
        // شمارش با indexOf
        let searchKeyword = this.keyword.toLowerCase();
        let count = 0;
        let pos = normalizedText.indexOf(searchKeyword);
        while (pos !== -1) {
          count++;
          pos = normalizedText.indexOf(searchKeyword, pos + searchKeyword.length);
        }
        keywordCount = count;
      }

      this.keywordCount = keywordCount;

      // محاسبه چگالی (تعداد تکرار / تعداد کل کلمات * 100)
      this.keywordDensity = this.wordCount > 0
          ? Math.round((keywordCount / this.wordCount) * 100 * 100) / 100
          : 0;

      // دیباگ: نمایش اطلاعات در کنسول

    },
    // ==========================================
    // 2. تحلیل کیفیت محتوا
    // ==========================================
    analyzeContentQuality(doc) {
      let score = 0;
      const details = {
        h1Count: 0,
        h2Count: 0,
        h3Count: 0,
        hierarchy: false,
        imageCount: 0,
        imagesWithAlt: 0,
        hasMetaDescription: false,
        metaDescriptionLength: 0,
        keywordInH1: false,
        keywordInMeta: false,
        keywordInTitle: false,
        keywordInUrl: false
      };

      // H1
      details.h1Count = doc.getElementsByTagName("h1").length;
      if (details.h1Count === 1) {
        score += 20;
      }

      // H2
      details.h2Count = doc.getElementsByTagName("h2").length;
      if (details.h2Count >= 4) {
        score += 20;
      } else if (details.h2Count >= 1) {
        score += 10;
      }

      // H3
      details.h3Count = doc.getElementsByTagName("h3").length;

      // ترتیب هدینگ
      details.hierarchy = this.validateHeadingHierarchy(doc);
      if (details.hierarchy) {
        score += 10;
      }

      // تصاویر
      const images = doc.getElementsByTagName("img");
      details.imageCount = images.length;

      if (details.imageCount >= 4) {
        score += 25;
      } else if (details.imageCount >= 1) {
        score += 15;
      }

      // Alt تصاویر
      let imagesWithAlt = 0;
      Array.from(images).forEach(img => {
        if (img.getAttribute("alt")) imagesWithAlt++;
      });
      details.imagesWithAlt = imagesWithAlt;

      if (imagesWithAlt === details.imageCount && details.imageCount > 0) {
        score += 15;
      } else if (details.imageCount > 0) {
        score += 5;
      }

      // متا توضیحات
      const metaDesc = doc.querySelector('meta[name="description"]');
      if (metaDesc) {
        details.hasMetaDescription = true;
        details.metaDescriptionLength = metaDesc.getAttribute("content")?.length || 0;
        if (details.metaDescriptionLength >= 120 && details.metaDescriptionLength <= 160) {
          score += 10;
        } else if (details.metaDescriptionLength > 0) {
          score += 5;
        }
      }

      // کلمه کلیدی در H1
      if (this.keyword && details.h1Count > 0) {
        const h1Text = doc.getElementsByTagName("h1")[0]?.innerText || "";
        details.keywordInH1 = h1Text.toLowerCase().includes(this.keyword.toLowerCase());
        if (details.keywordInH1) {
          score += 10;
        }
      }

      this.contentQuality = Math.min(score, 100);
      this.contentDetails = details;
    },

    // ==========================================
    // 3. تحلیل خوانایی
    // ==========================================
    analyzeReadability(doc) {
      let score = 0;
      const textContent = doc.body.textContent || "";
      const words = textContent.split(/\s+/).filter(w => w.length > 0);
      const wordCount = words.length;

      // طول محتوا
      if (wordCount >= 1500) {
        score += 30;
      } else if (wordCount >= 700) {
        score += 20;
      } else if (wordCount >= 300) {
        score += 10;
      }

      // طول جملات
      const sentences = textContent.split(/[.!?]+/).filter(s => s.trim().length > 0);
      const sentenceCount = sentences.length;
      const avgSentenceLength = sentenceCount > 0 ? Math.round(wordCount / sentenceCount) : 0;

      if (avgSentenceLength > 0 && avgSentenceLength <= 20) {
        score += 20;
      } else if (avgSentenceLength > 20 && avgSentenceLength <= 30) {
        score += 10;
      }

      // پاراگراف‌ها
      const paragraphs = textContent.split(/\n+/).filter(p => p.trim().length > 0);
      const paragraphCount = paragraphs.length;
      const avgParagraphLength = paragraphCount > 0 ? Math.round(wordCount / paragraphCount) : 0;

      if (avgParagraphLength > 0 && avgParagraphLength <= 100) {
        score += 15;
      } else if (avgParagraphLength > 0 && avgParagraphLength <= 200) {
        score += 8;
      }

      this.readabilityScore = Math.min(score, 100);
    },

    // ==========================================
    // 4. تحلیل سئو تکنیکال
    // ==========================================
    analyzeTechnicalSEO(doc) {
      let score = 0;

      // متا توضیحات
      const metaDesc = doc.querySelector('meta[name="description"]');
      if (metaDesc) {
        const length = metaDesc.getAttribute("content")?.length || 0;
        if (length >= 120 && length <= 160) {
          score += 20;
        } else if (length > 0) {
          score += 10;
        }
      }

      // کلمه کلیدی در متا
      if (this.keyword && metaDesc) {
        const content = metaDesc.getAttribute("content") || "";
        // *** اصلاح: کل عبارت را جستجو کن ***
        if (content.toLowerCase().includes(this.keyword.toLowerCase())) {
          score += 10;
        }
      }

      // کلمه کلیدی در H1
      if (this.keyword) {
        const h1Text = doc.getElementsByTagName("h1")[0]?.innerText || "";
        // *** اصلاح: کل عبارت را جستجو کن ***
        if (h1Text.toLowerCase().includes(this.keyword.toLowerCase())) {
          score += 15;
        }
      }

      // کلمه کلیدی در Title
      const titleTag = doc.querySelector("title");
      if (titleTag && this.keyword) {
        // *** اصلاح: کل عبارت را جستجو کن ***
        if (titleTag.innerText.toLowerCase().includes(this.keyword.toLowerCase())) {
          score += 10;
        }
      }

      // کلمه کلیدی در URL
      if (this.slug && this.keyword) {
        const cleanKeyword = this.keyword.toLowerCase().replace(/\s+/g, '-');
        if (this.slug.toLowerCase().includes(cleanKeyword)) {
          score += 10;
        }
      }

      // چگالی کلمه کلیدی (از تحلیل قبلی)
      if (this.keywordDensity >= 1 && this.keywordDensity <= 3) {
        score += 15;
      } else if (this.keywordDensity > 0 && this.keywordDensity < 5) {
        score += 8;
      }

      // لینک‌های داخلی
      if (this.internalLinks >= 3) {
        score += 10;
      } else if (this.internalLinks >= 1) {
        score += 5;
      }

      this.technicalSEO = Math.min(score, 100);
    },
    // ==========================================
    // 5. تحلیل لینک‌ها
    // ==========================================
    analyzeLinks(doc) {
      const links = doc.querySelectorAll('a');
      this.existingLinks = [];
      this.internalLinks = 0;
      this.externalLinks = 0;

      links.forEach(link => {
        const href = link.getAttribute('href');
        const text = link.textContent.trim();

        if (!href) return;

        const isInternal = href.includes('/') && (!href.includes('http') || href.includes(localStorage.getItem('web_address')));
        const target = isInternal ? 'داخلی' : 'خارجی';

        if (isInternal) {
          this.internalLinks++;
        } else {
          this.externalLinks++;
        }

        const isValid = this.validateLink(text, href, isInternal);

        this.existingLinks.push({
          text: text || 'بدون لنگر',
          target: target,
          href: href,
          isValid: isValid,
          suggestion: isValid ? null : this.getFixSuggestion(text, href)
        });
      });
    },

    // ==========================================
    // 6. پیدا کردن پیشنهادات لینک‌سازی
    // ==========================================
    findLinkSuggestions(doc) {
      this.linkSuggestions = [];

      const textContent = doc.body.textContent || '';
      const currentKeywordCount = this.countKeywords(this.keyword);

      if (!this.keyword || currentKeywordCount === 0) {
        return;
      }

      this.allArticles.forEach(article => {
        if (article.keyword === this.keyword) return;

        const articleKeywordCount = this.countKeywords(article.keyword);

        if (articleKeywordCount !== currentKeywordCount - 1) return;

        const relation = this.checkThematicRelation(this.keyword, article.keyword);

        if (!relation.isRelated) return;

        const hasLink = this.existingLinks.some(link =>
            link.href && link.href.includes(article.url)
        );

        if (hasLink) return;

        const bestPosition = this.findBestPosition(doc, article.keyword);

        this.linkSuggestions.push({
          anchor: article.title,
          targetUrl: article.url,
          targetKeyword: article.keyword,
          targetKeywordCount: articleKeywordCount,
          currentKeywordCount: currentKeywordCount,
          priority: 'high',
          message: `از "${this.title || this.keyword}" (${currentKeywordCount} کلمه) به "${article.title}" (${articleKeywordCount} کلمه) لینک دهید`,
          sampleText: `برای اطلاعات بیشتر در مورد ${article.title}، مقاله جامع ما را مطالعه کنید.`,
          position: bestPosition,
          relationType: relation.type,
          commonWords: relation.commonWords,
          suggestion: `لینک از "${this.keyword}" (${currentKeywordCount} کلمه) به "${article.keyword}" (${articleKeywordCount} کلمه) با لنگر "${article.title}"`
        });
      });

      this.linkSuggestions.sort((a, b) => {
        if (a.priority === 'high' && b.priority !== 'high') return -1;
        if (a.priority !== 'high' && b.priority === 'high') return 1;
        return 0;
      });
    },

    // ==========================================
    // توابع کمکی
    // ==========================================
    validateLink(anchorText, href, isInternal) {
      if (!isInternal) return true;
      if (!anchorText) return false;
      return anchorText.length > 0;
    },

    getFixSuggestion(anchorText, href) {
      if (!anchorText) {
        return 'لنگر (Anchor Text) را وارد کنید';
      }
      return 'لنگر را به کلمه کلیدی مرتبط تغییر دهید';
    },

    findBestPosition(doc, keyword) {
      const text = doc.body.textContent || '';
      const index = text.indexOf(keyword);

      if (index === -1) return { start: 0, end: 0 };

      const words = text.split(/\s+/);
      let wordIndex = 0;
      let currentIndex = 0;

      for (let i = 0; i < words.length; i++) {
        if (currentIndex >= index) {
          wordIndex = i;
          break;
        }
        currentIndex += words[i].length + 1;
      }

      return {
        start: wordIndex,
        end: wordIndex + 1,
        text: words[wordIndex] || ''
      };
    },

    validateHeadingHierarchy(doc) {
      const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');

      let lastLevel = 0;
      let hasH1 = false;

      for (const heading of headings) {
        const currentLevel = parseInt(heading.tagName.substring(1));

        if (currentLevel === 1) {
          if (hasH1) return false;
          hasH1 = true;
        }

        if (currentLevel > lastLevel + 1) return false;
        lastLevel = currentLevel;
      }

      return hasH1;
    },

    checkWarnings() {
      this.warnings = [];

      // ... بقیه هشدارها ...

      // *** اصلاح: هشدار چگالی کلمه کلیدی بر اساس تعداد واقعی ***
      if (this.keywordDensity > 3 && this.keywordDensity < 5) {
        this.warnings.push({
          type: 'warning',
          icon: 'cil-warning',
          message: `چگالی کلمه کلیدی (${this.keywordDensity}%) بالاتر از محدوده ایده‌آل است. سعی کنید آن را به 1-3% کاهش دهید. (${this.keywordCount} بار تکرار در ${this.wordCount} کلمه)`,
          action: null,
          actionLabel: null
        });
      } else if (this.keywordDensity >= 5) {
        this.warnings.push({
          type: 'danger',
          icon: 'cil-x',
          message: `⚠️ چگالی کلمه کلیدی (${this.keywordDensity}%) خیلی بالاست! ممکن است به عنوان کیورد استافینگ (Keyword Stuffing) تلقی شود. (${this.keywordCount} بار تکرار در ${this.wordCount} کلمه)`,
          action: null,
          actionLabel: null
        });
      } else if (this.keywordDensity < 1 && this.keywordDensity > 0) {
        this.warnings.push({
          type: 'warning',
          icon: 'cil-warning',
          message: `چگالی کلمه کلیدی (${this.keywordDensity}%) پایین است. سعی کنید کلمه کلیدی را بیشتر در محتوا تکرار کنید. (${this.keywordCount} بار تکرار در ${this.wordCount} کلمه)`,
          action: null,
          actionLabel: null
        });
      } else if (this.keywordDensity === 0 && this.keyword) {
        this.warnings.push({
          type: 'danger',
          icon: 'cil-x',
          message: `⚠️ کلمه کلیدی "${this.keyword}" در محتوا پیدا نشد! حتماً آن را به محتوا اضافه کنید.`,
          action: null,
          actionLabel: null
        });
      }
    },
    checkThematicRelation(keyword1, keyword2) {
      if (!keyword1 || !keyword2) {
        return { isRelated: false, commonWords: [], type: 'بدون ارتباط' };
      }

      const clean1 = this.removeStopWords(keyword1).join(' ');
      const clean2 = this.removeStopWords(keyword2).join(' ');

      if (!clean1 || !clean2) {
        return { isRelated: false, commonWords: [], type: 'بدون کلمه کلیدی معنی‌دار' };
      }

      const words1 = clean1.split(' ');
      const words2 = clean2.split(' ');

      const allWordsExist = words2.every(word => words1.includes(word));

      if (allWordsExist) {
        const commonWords = words2.filter(word => words1.includes(word));
        return {
          isRelated: true,
          commonWords: commonWords,
          type: `✅ مرتبط (همه ${commonWords.length} کلمه مشترک: ${commonWords.join('، ')})`
        };
      }

      return {
        isRelated: false,
        commonWords: [],
        type: '❌ بدون ارتباط کامل'
      };
    },

    removeStopWords(text) {
      if (!text) return [];

      const words = text.split(/\s+/).filter(w => w.length > 0);

      return words.filter(word => {
        const cleanWord = word.replace(/[^آ-یa-zA-Z]/g, '').toLowerCase();
        return !this.stopWords.includes(cleanWord) && cleanWord.length > 0;
      });
    },

    countKeywords(text) {
      if (!text) return 0;
      const filtered = this.removeStopWords(text);
      return filtered.length;
    },

    insertLink(suggestion) {
      this.selectedSuggestion = suggestion;
      this.showInsertModal = true;
    },

    applySuggestion(warning) {
      alert('پیشنهاد اعمال شد!');
    },

    fixLink(link) {
      alert(`لینک "${link.text}" اصلاح شد!`);
    },

    copyToClipboard(suggestion) {
      const text = suggestion.sampleText;

      navigator.clipboard.writeText(text).then(() => {
        alert('✅ متن کپی شد!');
      }).catch(() => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        alert('✅ متن کپی شد!');
      });
    },

    resetData() {
      this.wordCount = 0;
      this.internalLinks = 0;
      this.externalLinks = 0;
      this.linkDensity = 0;
      this.warnings = [];
      this.linkSuggestions = [];
      this.existingLinks = [];
      this.contentQuality = 0;
      this.readabilityScore = 0;
      this.technicalSEO = 0;
      this.keywordDensity = 0;
      this.keywordCount = 0;
      this.contentDetails = {
        h1Count: 0,
        h2Count: 0,
        h3Count: 0,
        hierarchy: false,
        imageCount: 0,
        imagesWithAlt: 0,
        hasMetaDescription: false,
        metaDescriptionLength: 0,
        keywordInH1: false,
        keywordInMeta: false,
        keywordInTitle: false,
        keywordInUrl: false
      };
    },

    // ==========================================
    // توابع نمایشی
    // ==========================================
    getHealthColor() {
      if (this.warnings.some(w => w.type === 'danger')) return 'danger';
      if (this.warnings.length > 0) return 'warning';
      return 'success';
    },

    getHealthStatus() {
      if (this.warnings.some(w => w.type === 'danger')) return '❌ نیاز به توجه';
      if (this.warnings.length > 0) return '⚠️ نیاز به بهبود';
      return '✅ عالی';
    },

    getScoreColor(score) {
      if (score >= 80) return 'success';
      if (score >= 60) return 'warning';
      return 'danger';
    },

    getScoreClass(score) {
      if (score >= 80) return 'text-success';
      if (score >= 60) return 'text-warning';
      return 'text-danger';
    },

    getCardQualityClass(score) {
      if (score >= 80) return 'border-success';
      if (score >= 60) return 'border-warning';
      return 'border-danger';
    },

    getQualityLabel(score) {
      if (score >= 80) return '✅ عالی';
      if (score >= 60) return '⚠️ خوب';
      return '❌ نیاز به بهبود';
    },

    getReadabilityLabel(score) {
      if (score >= 80) return '✅ خوانایی عالی';
      if (score >= 60) return '⚠️ خوانایی خوب';
      return '❌ خوانایی پایین';
    },

    getTechnicalLabel(score) {
      if (score >= 80) return '✅ سئو تکنیکال عالی';
      if (score >= 60) return '⚠️ قابل قبول';
      return '❌ نیاز به بهبود';
    },

    getWordCountClass(count) {
      if (count >= 1500) return 'text-success';
      if (count >= 700) return 'text-warning';
      return 'text-danger';
    },

    getWordCountLabel(count) {
      if (count >= 1500) return '✅ عالی';
      if (count >= 700) return '⚠️ خوب';
      return '❌ کم';
    },

    getInternalLinkClass(count) {
      if (count >= 3) return 'text-success';
      if (count >= 1) return 'text-warning';
      return 'text-danger';
    },

    getInternalLinkLabel(count) {
      if (count >= 3) return '✅ عالی';
      if (count >= 1) return '⚠️ قابل قبول';
      return '❌ نیاز به لینک';
    },

    // ==========================================
    // توابع چگالی کلمه کلیدی
    // ==========================================
    getDensityClass(density) {
      if (density >= 1 && density <= 3) return 'text-success';
      if (density > 0 && density < 5) return 'text-warning';
      return 'text-danger';
    },

    getDensityLabel(density) {
      if (density >= 1 && density <= 3) return '✅ ایده‌آل';
      if (density > 0 && density < 5) return '⚠️ قابل قبول';
      if (density >= 5) return '❌ زیاد';
      return '❌ کم';
    },

    getDensityBadgeColor(density) {
      if (density >= 1 && density <= 3) return 'success';
      if (density > 0 && density < 5) return 'warning';
      return 'danger';
    },

    getDensityProgressColor(density) {
      if (density >= 1 && density <= 3) return 'success';
      if (density > 3 && density < 5) return 'warning';
      return 'danger';
    },

    getDensityCardClass(density) {
      if (density >= 1 && density <= 3) return 'border-success';
      if (density > 0 && density < 5) return 'border-warning';
      return 'border-danger';
    }
  }
};
</script>

<style scoped>
.blog-link-suggestion {
  direction: rtl;
  text-align: right;
}

.text-value-lg {
  font-size: 2rem;
  font-weight: bold;
}

.list-group-item-success {
  background-color: #d4edda;
}

.list-group-item-danger {
  background-color: #f8d7da;
}

.border-success {
  border: 2px solid #28a745 !important;
}

.border-warning {
  border: 2px solid #ffc107 !important;
}

.border-danger {
  border: 2px solid #dc3545 !important;
}

.table-borderless td {
  border: none;
  padding: 4px 0;
}

.density-details {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.mr-1 {
  margin-left: 0.25rem !important;
}

.mr-2 {
  margin-left: 0.5rem !important;
}

.text-left {
  text-align: left !important;
}
</style>